import React, { useCallback, useEffect, useState } from 'react';
import { TextInput, Spinner } from 'flowbite-react';
import { useSearchParams } from 'react-router-dom';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { ToastContainer, toast } from 'react-toastify';
import Title from '../../components/Title';
import {
  DraftVersion,
  SetCurrentBookDraftResponse,
  getBookDraftVersions,
  setCurrentBookDraft,
} from '../../api/bookDrafts';
import { bookDraftsStrings as S, formatMessage } from './BookDrafts.strings';

// Internal, URL-only support page (not linked in the sidebar): list every draft
// version of a member's book and switch which one the editor opens. The book is found
// from a support-friendly (email, draftId) pair — the backend validates the draft
// belongs to that customer. Reversible and history-preserving — nothing is deleted;
// "revert" is just making another version current again. Editor-only: the public
// <sub>.book.live reader (Pagestead) is NOT affected; that would need a re-publish.

const shortId = (id?: string | null): string =>
  id ? `${id.slice(0, 6)}…${id.slice(-4)}` : '';

const fmtDate = (d?: string): string => (d ? new Date(d).toLocaleString() : '—');

const extractMsg = (err: unknown, fallback: string): string => {
  const axiosErr = err as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return axiosErr?.response?.data?.message || axiosErr?.message || fallback;
};

// Plain-words outcome of a set-current. `changed` is false when the resulting current
// draft is the one that was already current (e.g. a reused copy that was current).
const describeSwitch = (
  data: SetCurrentBookDraftResponse,
  changed: boolean,
): string => {
  const copyId = shortId(data.copiedDraftId);
  if (data.copiedDraftId) {
    if (!data.copyReused) return formatMessage(S.newCopyMadeCurrent, { copyId });
    return formatMessage(
      changed ? S.existingCopyMadeCurrent : S.existingCopyAlreadyCurrent,
      { copyId },
    );
  }
  return changed
    ? S.switched
    : formatMessage(S.alreadyCurrent, { id: shortId(data.currentDraftId) });
};

const BookDrafts: React.FC = () => {
  const { user } = useFusionAuth();
  const operatorEmail = user?.email || '';

  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [draftId, setDraftId] = useState(searchParams.get('draftId') || '');
  const [book, setBook] = useState<{ userId: string; bookId: string } | null>(null);
  const [versions, setVersions] = useState<DraftVersion[]>([]);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [previousCurrentId, setPreviousCurrentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const em = email.trim();
    const did = draftId.trim();
    if (!em || !did) {
      setVersions([]);
      setCurrentDraftId(null);
      setBook(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getBookDraftVersions(em, did, operatorEmail);
      setVersions(data.versions || []);
      setCurrentDraftId(data.currentDraftId);
      setBook({ userId: data.userId, bookId: data.bookId });
    } catch (err) {
      const msg = extractMsg(err, S.loadError);
      setError(msg);
      setVersions([]);
      setCurrentDraftId(null);
      setBook(null);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [email, draftId, operatorEmail]);

  // Auto-load when deep-linked with ?email=&draftId=
  useEffect(() => {
    if (searchParams.get('email') && searchParams.get('draftId')) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoad = () => {
    setPreviousCurrentId(null);
    setSearchParams({ email: email.trim(), draftId: draftId.trim() });
    load();
  };

  const handleMakeCurrent = async (targetDraftId: string) => {
    setSwitchingId(targetDraftId);
    const prev = currentDraftId;
    try {
      const data = await setCurrentBookDraft(targetDraftId, operatorEmail);
      const changed = data.currentDraftId !== prev;
      setVersions(data.versions || []);
      setCurrentDraftId(data.currentDraftId);
      setBook({ userId: data.userId, bookId: data.bookId });
      // Undo restores the draft that was current before a real change. On a no-op
      // keep any earlier Undo, but never offer one that "restores" the current draft.
      setPreviousCurrentId((existing) => {
        const next = changed ? prev : existing;
        return next && next !== data.currentDraftId ? next : null;
      });
      const message = describeSwitch(data, changed);
      if (changed) toast.success(message);
      else toast.info(message);
    } catch (err) {
      toast.error(extractMsg(err, S.switchError));
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* No app-wide ToastContainer exists, so this page mounts its own (as Offers does). */}
      <ToastContainer />
      <Title>Book Draft Switcher</Title>
      <p className="text-sm text-gray-600">
        Internal support tool. Enter the customer&apos;s email and any draft ID of the
        book, then choose which version the <strong>editor</strong> opens. Reversible and
        history-preserving — no version is ever deleted. The public <code>book.live</code>{' '}
        reader is not affected (that needs a re-publish). After switching, ask the
        customer to reopen the book from a fresh page load.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
            Customer email
          </label>
          <TextInput
            type="email"
            placeholder="customer@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="w-80">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
            Draft ID
          </label>
          <TextInput
            placeholder="any draftId of the book"
            value={draftId}
            onChange={(e) => setDraftId(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="h-10 px-4 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          onClick={handleLoad}
          disabled={loading || !email.trim() || !draftId.trim()}
        >
          Load versions
        </button>
      </div>

      {book && !loading && (
        <div className="text-xs text-gray-500">
          Book <span className="font-mono">{book.bookId}</span> · user{' '}
          <span className="font-mono">{book.userId}</span> · {versions.length} version(s)
        </div>
      )}

      {previousCurrentId && (
        <div className="text-sm">
          <button
            type="button"
            className="px-3 py-1 rounded border border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            onClick={() => handleMakeCurrent(previousCurrentId)}
            disabled={switchingId !== null}
          >
            {formatMessage(S.undo, { id: shortId(previousCurrentId) })}
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center text-sm text-gray-600">
          <Spinner size="sm" className="mr-2" />
          Loading versions…
        </div>
      )}

      {error && !loading && <div className="text-sm text-red-600">{error}</div>}

      {!loading && !error && versions.length === 0 && (email || draftId) && (
        <div className="text-sm text-gray-500">
          No versions found. Enter a customer email + a draft ID above.
        </div>
      )}

      {versions.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  'Thumbnail',
                  'Name',
                  'Draft ID',
                  'Milestone',
                  'Status',
                  'Links',
                  'Updated',
                  'Preview',
                  'Action',
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {versions.map((v) => (
                <tr
                  key={v.draftId}
                  className={v.isCurrent ? 'bg-green-50' : undefined}
                >
                  <td className="px-4 py-3">
                    {v.thumbnailUrl ? (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt
                      <img
                        src={v.thumbnailUrl}
                        alt="cover"
                        className="h-16 w-auto rounded shadow-sm"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">none</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {v.name}
                    {v.parentDraftId && (
                      <div
                        className="mt-1 text-xs text-gray-500"
                        title={v.parentDraftId}
                      >
                        {formatMessage(S.copyOf, { id: shortId(v.parentDraftId) })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-600">
                    {shortId(v.draftId)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{v.milestone}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{v.status}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {v.linksStatus || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {fmtDate(v.updatedAt)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {v.previewUrl ? (
                      <a
                        href={v.previewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        PDF
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {v.isCurrent ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">
                        CURRENT
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                        onClick={() => handleMakeCurrent(v.draftId)}
                        disabled={switchingId !== null}
                      >
                        {switchingId === v.draftId ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Switching…
                          </>
                        ) : (
                          'Make current'
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BookDrafts;
